import { Component, Input } from '@angular/core';
import { AbregelungKopplungsTyp } from '../../../../../domain/model/abregelung-kopplungs-typ.enum';
import {
    AbregelungsinformationenTase2Adressen,
} from '../../../../../domain/model/abregelungsinformationen-tase2-adressen.model';
import { AbstractControlOptions, UntypedFormGroup, Validators } from '@angular/forms';
import { RowDescriptor } from '../../row-descriptor';
import { ErzeugungsanlageConfig } from '../../../erzeugungsanlage.config';
import { ControlConfig, FormGroupConfig } from '../../../form-config.model';
import { MatValidators } from '../../../../../common/material/components/mat-form-field-required.directive';
import { Tase2Validator } from '../../../validator/tase2.validator';
import { Feld } from '../../../../../domain/model/feld.enum';

interface FeldMapping {
    kopplungsTyp: Feld
    tase2Objekt: Feld
    elementTyp: Feld
}

@Component({
    selector: 'redispatch-abregelinformationen-tase2-adresse',
    templateUrl: './abregelinformationen-tase2-adresse.component.html',
    styleUrls: [
        './abregelinformationen-tase2-adresse.component.scss',
    ],
    standalone: false,
})
export class AbregelinformationenTase2AdresseComponent {

    public static readonly TASE2_OBJECT_PATTERN = new RegExp('^[a-zA-Z$_]{1}[a-zA-Z0-9$_]{0,31}$');
    public static readonly ELEMENT_TYP_PATTERN = new RegExp('^.{1,16}$');

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly COLS = ErzeugungsanlageConfig.ABREGELINFOS_ADDR_TASE_FX_FLEX_COL;
    public readonly Feld = Feld;

    @Input({ required: true }) form: UntypedFormGroup;
    @Input({ required: true }) disabled: boolean;
    @Input({ required: true }) readonly: boolean;

    private readonly feldEntityMapping: {
        [params: string]: FeldMapping
    } = {
            SOLLWERT: {
                kopplungsTyp: Feld.FIELD_TASE2_SOLLWERT_KOPPLUNGSTYP,
                tase2Objekt: Feld.FIELD_TASE2_SOLLWERT_TASE2OBJEKT,
                elementTyp: Feld.FIELD_TASE2_SOLLWERT_ELEMENTTYP,
            },
            TYP_100: {
                kopplungsTyp: Feld.FIELD_TASE2_100_KOPPLUNGSTYP,
                tase2Objekt: Feld.FIELD_TASE2_100_TASE2OBJEKT,
                elementTyp: Feld.FIELD_TASE2_100_ELEMENTTYP,
            },
            TYP_60: {
                kopplungsTyp: Feld.FIELD_TASE2_60_KOPPLUNGSTYP,
                tase2Objekt: Feld.FIELD_TASE2_60_TASE2OBJEKT,
                elementTyp: Feld.FIELD_TASE2_60_ELEMENTTYP,
            },
            TYP_30: {
                kopplungsTyp: Feld.FIELD_TASE2_30_KOPPLUNGSTYP,
                tase2Objekt: Feld.FIELD_TASE2_30_TASE2OBJEKT,
                elementTyp: Feld.FIELD_TASE2_30_ELEMENTTYP,
            },
            TYP_0: {
                kopplungsTyp: Feld.FIELD_TASE2_0_KOPPLUNGSTYP,
                tase2Objekt: Feld.FIELD_TASE2_0_TASE2OBJEKT,
                elementTyp: Feld.FIELD_TASE2_0_ELEMENTTYP,
            },
            WIRKLEISTUNG_P: {
                kopplungsTyp: Feld.FIELD_TASE2_WIRKLEISTUNG_KOPPLUNGSTYP,
                tase2Objekt: Feld.FIELD_TASE2_WIRKLEISTUNG_TASE2OBJEKT,
                elementTyp: Feld.FIELD_TASE2_WIRKLEISTUNG_ELEMENTTYP,
            },
            BLINDLEISTUNG_Q: {
                kopplungsTyp: Feld.FIELD_TASE2_BLINDLEISTUNG_KOPPLUNGSTYP,
                tase2Objekt: Feld.FIELD_TASE2_BLINDLEISTUNG_TASE2OBJEKT,
                elementTyp: Feld.FIELD_TASE2_BLINDLEISTUNG_ELEMENTTYP,
            },
        };

    constructor() {
    }

    public get f(): ControlConfig<AbregelungsinformationenTase2Adressen> {
        return this.form.controls as ControlConfig<AbregelungsinformationenTase2Adressen>;
    }

    public get kopplungsTyp(): AbregelungKopplungsTyp {
        return this.f.kopplungsTyp.value;
    }

    get rowDesc(): RowDescriptor {
        return RowDescriptor.getNameForType(this.kopplungsTyp);
    }

    get placeholder(): string {
        if (!this.f.tase2Objekt.value && !this.f.elementTyp.value) {
            if (!(this.form as any).customPassthroughRequired) {
                return 'Wert optional';
            }
        }
        return 'Wert eintragen';
    }

    get warningTooltip(): string {
        return this.form.hasError('fillAllValues') ? 'Bitte füllen Sie alle der Felder aus' : '';
    }

    get feldMapping(): FeldMapping | null {
        return this.feldEntityMapping[this.kopplungsTyp];
    }

    static formGroupConfig(
        initType: AbregelungKopplungsTyp,
        initialDisabled: boolean,
        required: boolean,
    ): FormGroupConfig<AbregelungsinformationenTase2Adressen> {
        const validatorTase2Object = [
            Validators.pattern(AbregelinformationenTase2AdresseComponent.TASE2_OBJECT_PATTERN),
        ];
        const validatorElementTyp = [
            Validators.pattern(AbregelinformationenTase2AdresseComponent.ELEMENT_TYP_PATTERN),
            Validators.minLength(1),
            Validators.maxLength(16),
        ];

        if (required) {
            validatorTase2Object.push(MatValidators.required);
            validatorElementTyp.push(MatValidators.required);
        }

        return {
            kopplungsTyp: [
                { value: initType, disabled: initialDisabled },
                [
                    MatValidators.required,
                ],
            ],
            tase2Objekt: [
                { value: null, disabled: initialDisabled },
                [
                    ...validatorTase2Object,
                ],
            ],
            elementTyp: [
                { value: null, disabled: initialDisabled },
                [
                    ...validatorElementTyp,
                ],
            ],
        } as FormGroupConfig<AbregelungsinformationenTase2Adressen>;
    }

    static formOptions(required: boolean): AbstractControlOptions | null {
        return {
            validators: Tase2Validator.allFieldsPresentValidator(required),
        };
    }
}
